import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VerifiedIcon from '@mui/icons-material/Verified';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupsIcon from '@mui/icons-material/Groups';
import storyImage from '../../assets/images/about/4.png';

// ============================================================
// ANIMATIONS
// ============================================================

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(10, 79, 213, 0.5);
  }
  70% {
    box-shadow: 0 0 0 25px rgba(10, 79, 213, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(10, 79, 213, 0);
  }
`;

const rotate = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`;

// ============================================================
// COLOR PALETTE
// ============================================================

const DEEP_BLUE = "#0a1f44";
const PRIMARY_BLUE = "#0a4fd5";
const LIGHT_BLUE = "#4a9eff";
const SOFT_BLUE = "#e8f2ff";
const BORDER_BLUE = "#d4e5ff";
const TEXT_MUTED = "#5a6b85";

// ============================================================
// STYLED COMPONENTS
// ============================================================

const SectionWrapper = styled(Box)({
  position: "relative",
  padding: "80px 20px",
  backgroundColor: "#f8fafc",
  overflow: "hidden",
});

const StoryContainer = styled(Box)({
  maxWidth: "1200px",
  margin: "0 auto",
  position: "relative",
  zIndex: 2,
});

const VideoCard = styled(Box)({
  position: "relative",
  borderRadius: "24px",
  overflow: "hidden",
  boxShadow: "0 25px 70px rgba(10, 31, 68, 0.15)",
  animation: `${fadeInUp} 1s ease forwards`,
  transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",

  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(135deg, rgba(10, 31, 68, 0.1) 0%, rgba(10, 79, 213, 0.15) 100%)",
    zIndex: 1,
    transition: "opacity 0.4s ease",
  },

  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 35px 90px rgba(10, 79, 213, 0.25)",

    "&::before": {
      opacity: 0.5,
    },

    "& .story-image": {
      transform: "scale(1.05)",
    },

    "& .play-button": {
      transform: "translate(-50%, -50%) scale(1.15)",
      backgroundColor: PRIMARY_BLUE,
    },
  },
});

const StoryImage = styled("img")({
  width: "100%",
  height: "auto",
  display: "block",
  objectFit: "cover",
  transition: "transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
});

const PlayButtonWrapper = styled("a")({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 90,
  height: 90,
  borderRadius: "50%",
  backgroundColor: DEEP_BLUE,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#ffffff",
  zIndex: 3,
  textDecoration: "none",
  animation: `${pulse} 2.5s infinite`,
  boxShadow: "0 10px 40px rgba(10, 31, 68, 0.4)",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",

  "& svg": {
    fontSize: 42,
    marginLeft: "4px",
    transition: "transform 0.3s ease",
  },

  "&:hover svg": {
    transform: "scale(1.15)",
  },
});

const FloatingBadge = styled(Box)(({ position }) => ({
  position: "absolute",
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "14px 22px",
  borderRadius: "14px",
  backgroundColor: "#ffffff",
  boxShadow: "0 15px 40px rgba(10, 31, 68, 0.12)",
  border: `1px solid ${BORDER_BLUE}`,
  zIndex: 3,
  animation: `${float} 3.5s ease-in-out infinite`,
  animationDelay: position === "top" ? "0s" : "1.5s",

  ...(position === "top" && {
    top: 30,
    right: 30,
  }),

  ...(position === "bottom" && {
    bottom: 30,
    left: 30,
  }),

  "@media (max-width: 600px)": {
    padding: "10px 16px",
    ...(position === "top" && { top: 16, right: 16 }),
    ...(position === "bottom" && { bottom: 16, left: 16 }),
  },
}));

const BadgeIcon = styled(Box)(({ color }) => ({
  width: 40,
  height: 40,
  borderRadius: "50%",
  backgroundColor: color === "blue" ? SOFT_BLUE : "rgba(74, 158, 255, 0.1)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: color === "blue" ? PRIMARY_BLUE : LIGHT_BLUE,

  "& svg": {
    fontSize: 22,
  },
}));

const DecorativeCircle = styled(Box)(({ size, top, left, right, bottom, delay }) => ({
  position: "absolute",
  width: size,
  height: size,
  borderRadius: "50%",
  border: `2px dashed ${BORDER_BLUE}`,
  top,
  left,
  right,
  bottom,
  opacity: 0.6,
  animation: `${rotate} ${20 + (delay || 0)}s linear infinite`,
  pointerEvents: "none",
  zIndex: 1,
}));

const DecorativeBlob = styled(Box)(({ top, left, right, bottom, color }) => ({
  position: "absolute",
  width: 400,
  height: 400,
  borderRadius: "50%",
  background: color,
  filter: "blur(80px)",
  top,
  left,
  right,
  bottom,
  opacity: 0.15,
  pointerEvents: "none",
  zIndex: 0,
}));

// ============================================================
// MAIN COMPONENT
// ============================================================

const Story = () => {
  return (
    <SectionWrapper>
      {/* Decorative Background Elements */}
      <DecorativeBlob top="-100px" right="-100px" color={PRIMARY_BLUE} />
      <DecorativeBlob bottom="-150px" left="-100px" color={LIGHT_BLUE} />

      {/* Rotating Decorative Circles */}
      <DecorativeCircle size={150} top="10%" left="5%" delay={0} />
      <DecorativeCircle size={100} bottom="15%" right="8%" delay={10} />

      <StoryContainer>
        <VideoCard>
          {/* Story Image */}
          <StoryImage
            className="story-image"
            src={storyImage}
            alt="Our Story"
          />

          {/* Floating Badge - Top Right */}
          <FloatingBadge position="top">
            <BadgeIcon color="blue">
              <VerifiedIcon />
            </BadgeIcon>
            <Box>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: TEXT_MUTED,
                  fontWeight: 500,
                  lineHeight: 1.2,
                }}
              >
                Trusted by
              </Typography>
              <Typography
                sx={{
                  fontSize: "15px",
                  color: DEEP_BLUE,
                  fontWeight: 700,
                  lineHeight: 1.3,
                }}
              >
                5,000+ Traders
              </Typography>
            </Box>
          </FloatingBadge>

          {/* Floating Badge - Bottom Left */}
          <FloatingBadge position="bottom">
            <BadgeIcon color="light">
              <TrendingUpIcon />
            </BadgeIcon>
            <Box>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: TEXT_MUTED,
                  fontWeight: 500,
                  lineHeight: 1.2,
                }}
              >
                Profit Generated
              </Typography>
              <Typography
                sx={{
                  fontSize: "15px",
                  color: DEEP_BLUE,
                  fontWeight: 700,
                  lineHeight: 1.3,
                }}
              >
                $2M+ Monthly
              </Typography>
            </Box>
          </FloatingBadge>

          {/* Play Button */}
          <PlayButtonWrapper
            className="play-button"
            href="https://www.youtube.com/watch?v=uJSgaPIvgKk"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Play our story video"
          >
            <PlayArrowIcon />
          </PlayButtonWrapper>
        </VideoCard>
      </StoryContainer>
    </SectionWrapper>
  );
};

export default Story;